import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {Switch} from "../../../../../../components/base/switch";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, Card, FormControlLabel, IconButton, Skeleton, Tooltip} from "@mui/material";
import Api from "../../../../../../../core/services/api/api";
import ConfigurableInfoBlock, {ConfigurableInfoBlockFeedbackIconPositions, ConfigurableInfoBlockInputTypes} from "../../../../../../components/app-specific/configurable-info-block";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import ProductEntryHistoryDialog from "../../../../../../components/dialogs/product-entry-history";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import TagChipsContent from "../../../../../../components/app-specific/chips-contents/tag-chips-content";
import PartFormChipsContent from "../../../../../../components/app-specific/chips-contents/part-form-chips-content";
import {MarkProductForCacheSectionNames} from "../../../../../../../core/constants/enums";
import {Link, useParams} from "react-router-dom";
import Grid2 from "@mui/material/Unstable_Grid2";
import {InfoOutlined, OpenInNew} from "@mui/icons-material";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {ProductInformationTabs} from "../index";
import {useBizFormApiResolver} from "../../../../../../hooks/use-biz-form-api-resolver";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import useDeliveryMode from "../../../../../../hooks/use-delivery-mode";
import {useTranslation} from "react-i18next";
import ProductApplicablePriceMatrices from "../../../../common/price-matrices/product-applicable";

const ProductInformationBasicInformationView = ({product, setProduct, loading, setIsCacheNeeded}) => {
    const params = useParams()
    const [togglingIsSerialized, setTogglingIsSerialized] = useState(false);
    const [togglingIsPublic, setTogglingIsPublic] = useState(false);
    const [togglingIsVisible, setTogglingIsVisible] = useState(false);
    const [togglingIsVisibleForClient, setTogglingIsVisibleForClient] = useState(false);
    const [togglingIsEligibilityForOnlineOrdering, setTogglingIsEligibilityForOnlineOrdering] = useState(false);
    const [openCostChart, setOpenCostChart] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const {options: deliveryModeOptions, getLabel: getDeliveryModeLabel} = useDeliveryMode();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.product-basic-information'});

    const productId = React.useMemo(() => Number(params.id), [params.id]);

    const suggestedTags = useMemo(() => {
        const tagIds = product?.tags?.map(e => e.id) ?? [];
        return product?.suggestedTags?.filter(e => !tagIds.includes(e.id)) ?? [];
    }, [product?.tags, product?.suggestedTags]);

    /**
     * Toggles this product's isSerializable property in the server.
     * * updates the new value in the state as well if the response of the api is successful
     * @type {function(): Promise<void>}
     */
    const toggleProductSerializability = useCallback(async () => {
        setTogglingIsSerialized(true);
        const response = await Api.toggleProductBeingSerialized(productId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                isSerializable: response.data?.isSerialized ?? false,
            }))
        }
        setTogglingIsSerialized(false);
    }, [productId, setProduct, isMounted])

    /**
     * Toggles this product's isVisible property in the server.
     * * updates the new value in the state as well if the response of the api is successful.
     * @type {function(): Promise<void>}
     */
    const toggleProductVisibility = useCallback(async () => {
        setTogglingIsVisible(true);
        const response = await Api.toggleProductVisibility(productId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                isVisible: !prevState.isVisible,
            }))
            setIsCacheNeeded?.(true)
        }
        setTogglingIsVisible(false);
    }, [productId, isMounted, setProduct, setIsCacheNeeded])

    /**
     * Toggles this product's isVisible property in the server.
     * * updates the new value in the state as well if the response of the api is successful.
     * @type {function(): Promise<void>}
     */
    const toggleIsVisibleForClient = useCallback(async (value) => {
        setTogglingIsVisibleForClient(true);
        const response = await Api.toggleProductIsVisibleForClient({
            partId: productId,
            isVisibleForClient: value,
        });
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                isVisibleForClient: value,
            }))
        }
        setTogglingIsVisibleForClient(false);
    }, [productId, isMounted, setProduct])

    /**
     * Toggles this product's isVisible property in the server.
     * * updates the new value in the state as well if the response of the api is successful.
     * @type {function(): Promise<void>}
     */
    const toggleProductShareStatus = useCallback(async () => {
        setTogglingIsPublic(true);
        const response = await Api.setProductIsPublicForBuyers({
            partId: productId,
            isPublicForBuyers: !product.isPublicForBuyers,
        });
        if (!isMounted()) return;
        setTogglingIsPublic(false);
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                isPublicForBuyers: !product.isPublicForBuyers,
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.shareStatus]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
    }, [productId, product?.isPublicForBuyers, setProduct, isMounted, setIsCacheNeeded])


    /**
     * Toggles this product's eligibility for online ordering in the server.
     * @type {(function(*): Promise<void>)|*}
     */
    const toggleIsEligibilityForOnlineOrdering = useCallback(async (value) => {
        setTogglingIsEligibilityForOnlineOrdering(true);
        const response = await Api.toggleProductEligibilityForOnlineOrdering({
            partId: productId,
            isEligibleForOnlineOrdering: value,
        });
        if (!isMounted()) return;
        setTogglingIsEligibilityForOnlineOrdering(false);
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                isEligibleForOnlineOrdering: value,
            }))
        }
    }, [productId, setProduct, isMounted])

    /**
     * Updates the upc number of the current product in the server.
     * @type {(upc: string) => Promise<* | undefined>}
     */
    const updatePartUPC = useCallback(async (upc) => {
        if (typeof upc === 'string' && upc?.trim().length === 0)
            return {
                resultFlag: false,
                message: "Field cannot be empty"
            }
        const forApi = {
            upc: upc,
            partId: productId,
        }
        const response = await Api.updateOperatorPartUPC(forApi, true);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                upc: upc,
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.upc]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        return response;
    }, [productId, setProduct, isMounted, setIsCacheNeeded])

    /**
     * Updates the title of the current product in the server.
     * @type {(function(*): Promise<{resultFlag: boolean, message: string}|undefined|{resultFlag: boolean}>)|*}
     */
    const updatePartTitle = useCallback(async (title) => {
        if (typeof title === 'string' && title?.trim().length === 0)
            return {
                resultFlag: false,
                message: "Field cannot be empty"
            }
        const response = await Api.updatePartTitle({
            partId: productId,
            title: title,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                title: title,
            }))
        }
        return response;
    }, [productId, setProduct, isMounted])

    /**
     * Updates the upc number of the current product in the server.
     * @type {(cost: string) => Promise<* | undefined>}
     */
    const updatePartCost = useCallback(async (cost) => {
        if (typeof cost === 'string' && cost?.trim().length === 0)
            return {
                resultFlag: false,
                message: "Field cannot be empty"
            }
        const forApi = {
            partId: productId,
            cost: Number(cost),
        }
        if (forApi.cost === product.cost)
            return;
        const response = await Api.updateProductCost(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                cost: parseFloat(cost),
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.cost]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        return response;
    }, [productId, product.cost, setProduct, isMounted, setIsCacheNeeded])

    /**
     * Updates the category of a product in the server.
     * @type {(categoryId: number) => Promise<* | undefined>}
     */
    const updatePartCategory = useCallback(async (categoryId) => {
        const forApi = {
            categoryId: categoryId,
            partId: productId,
        }
        const response = await Api.updateOperatorPartCategory(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                category: response.data,
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.category]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        return response;
    }, [productId, setProduct, isMounted, setIsCacheNeeded])

    /**
     * Updates the delivery mode of a product in the server.
     * @type {(deliveryMode: number) => Promise<* | undefined>}
     */
    const updatePartDeliveryMode = useCallback(async (deliveryMode) => {
        const forApi = {
            partId: productId,
            deliveryMode: deliveryMode,
        };
        const response = await Api.updatePartDeliveryMode(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                deliveryMode: deliveryMode,
            }));
        }
        return response;
    }, [productId, setProduct, isMounted]);

    /**
     * Updates the description of a product in the server.
     * @type {(description: string) => Promise<* | undefined>}
     */
    const updatePartDescription = useCallback(async (description) => {
        const forApi = {
            description: description,
            partId: productId,
        }
        const response = await Api.upsertOperatorPartDescription(forApi, true);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                description: description,
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.description]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        return response;
    }, [productId, setProduct, isMounted, setIsCacheNeeded])

    /**
     * Updates the description of a product in the server.
     * @type {(description: string) => Promise<* | undefined>}
     */
    const updateShortDescription = useCallback(async (description) => {
        const forApi = {
            description: description,
            partId: productId,
        }
        const response = await Api.updateOperatorPartShortDescription(forApi, true);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                shortDescription: description,
            }))
        }
        return response;
    }, [productId, setProduct, isMounted])

    /**
     * Fetches the cost history of the current product with the provided filters.
     * @type {(filters: any) => Promise<{data: {dateLabel: *, title: string, content: *[]}, resultFlag: boolean}|{data: (*[]|undefined), resultFlag: boolean}>}
     */
    const getPartCostHistory = useCallback(async (filters) => {
        const forApi = {
            ...filters,
            part: productId,
        }
        const response = await Api.getProductCostHistoryReport(forApi);
        return {
            resultFlag: response?.resultFlag,
            dateLabel: 'Date',
            dateExportOrderIndex: 2,
            data: [
                {
                    data: response?.data?.map(e => ({
                        date: e.submitDateTime,
                        entry: e.value,
                        extra: {
                            operator: e.operator,
                        },
                    })),
                    label: 'Cost',
                    formatValue: formatMoney,
                    exportOrderIndex: 1,
                }
            ],
            exportSpecific: [
                {
                    label: 'Operator',
                    valueGetter: (e) => e?.operator ?? '',
                    orderIndex: 0,
                }
            ],
        };
    }, [productId])

    /**
     * Fetches the cost history of the current product to be used for exporting purposes.
     * @type {(filters: any) => Promise<{data: *[], resultFlag: boolean, title: string}>}
     */
    const getPartCostHistoryForExport = useCallback(async (filters) => {
        const forApi = {
            ...filters,
            partId: productId,
        }
        const response = await Api.getProductCostHistoryReport(forApi);
        if (!isMounted())
            return {resultFlag: false};
        if (!response.resultFlag) {
            // just pass it along since nothing will be generated
            return response;
        }
        const title = `Cost History of ${product?.partNo ?? ''}`;
        const data = response.data?.map((e, index) => ({
            "": index + 1,
            "Old Cost": e.oldCost ? formatMoney(e.oldCost) : '--',
            "New Cost": e.newCost ? formatMoney(e.newCost) : '--',
            "Submitted Datetime": e.submitDateTime ?? '--',
            "Operator": e.operatorName ?? '--',
        })) ?? [];
        return {
            resultFlag: response?.resultFlag,
            data: data,
            title: title,
        };
    }, [productId, product?.partNo, isMounted])

    /**
     * Adds tag to the list of part's tags in the server.
     * @type {(tag: any) => Promise<void>}
     */
    const addPartTag = useCallback(async (tag) => {
        const forApi = {
            partId: productId,
            tagId: tag.id
        }
        const response = await Api.addTagToOperatorPart(forApi);
        const statsForApi = {
            ids: [tag.id]
        }
        const statsResponse = await Api.getTagsStatistics(statsForApi);
        if (!isMounted())
            return;
        if (statsResponse?.resultFlag) {
            tag = {
                ...tag,
                repeatCount: (statsResponse?.data?.[0]?.numberOfConnectedPart ?? 0) + (statsResponse?.data?.[0]?.numberOfConnectedBanner ?? 0),
            }
        }
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                tags: [...prevState.tags ?? [], {
                    ...tag,
                    name: response?.data?.name ?? '',
                    repeatCount: tag.repeatCount ?? 1
                }],
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.tag]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
    }, [productId, setProduct, isMounted, setIsCacheNeeded])

    /**
     * Removes the tag from the list of connected tags to a part in the server
     * @tyle {(tag: any) => Promise<void>}
     */
    const removePartTag = useCallback(async (tag) => {
        const forApi = {
            partId: productId,
            tagId: tag.id
        }
        const response = await Api.removeTagFromOperatorPart(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setProduct(prevState => ({
                ...prevState,
                tags: prevState.tags.filter(e => e.id !== tag.id)
            }))

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.tag]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
    }, [productId, setProduct, isMounted, setIsCacheNeeded])

    const formsApi = useBizFormApiResolver((t) => t.operatorApp);
    const [togglingPartFormRequiredMap, setTogglingPartFormRequiredMap] = useState({});

    const partForms = useMemo(() => product?.partForms ?? [], [product?.partForms]);

    /**
     * Loads part-form associations for this product.
     */
    const loadPartForms = useCallback(async () => {
        if (!productId) {
            return;
        }
        const response = await Api.listPartForms(productId);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setProduct((prevState) => ({
                ...prevState,
                partForms: response.data ?? [],
            }));
        }
    }, [isMounted, productId, setProduct]);

    /**
     * Loads part-form associations on component mount and whenever the productId changes.
     */
    useEffect(() => {
        if (loading || !productId || !operatorAccessLevels.packages.lookup.manageProductForms) {
            return;
        }
        void loadPartForms();
    }, [loadPartForms, loading, operatorAccessLevels.packages.lookup.manageProductForms, productId]);

    /**
     * Adds a form association to the part.
     * @type {(function(*): Promise<void>)|*}
     */
    const addPartForm = useCallback(async (form) => {
        const response = await Api.addPartForm({
            partId: productId,
            formId: form.id,
            isRequired: false,
        });
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag && response.data) {
            setProduct((prevState) => ({
                ...prevState,
                partForms: [
                    ...(prevState.partForms ?? []),
                    {
                        partFormId: response.data.partFormId,
                        partId: response.data.partId,
                        isRequired: response.data.isRequired,
                        form: {
                            id: form.id,
                            title: form.title,
                            description: form.description,
                            isPublic: form.isPublic,
                        },
                    },
                ],
            }));
        }
    }, [isMounted, productId, setProduct]);

    /**
     * Removes a form association from the part.
     * @type {(function(*): Promise<void>)|*}
     */
    const removePartForm = useCallback(async (partForm) => {
        const response = await Api.deletePartForm(partForm.partFormId);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setProduct((prevState) => ({
                ...prevState,
                partForms: (prevState.partForms ?? []).filter((e) => e.partFormId !== partForm.partFormId),
            }));
        }
    }, [isMounted, setProduct]);

    /**
     * Toggles whether a part-form association is required.
     * @type {(function(*): Promise<void>)|*}
     */
    const togglePartFormRequired = useCallback(async (partForm) => {
        const nextRequired = !partForm.isRequired;
        setTogglingPartFormRequiredMap((prev) => ({...prev, [partForm.partFormId]: true}));
        const response = await Api.updatePartForm({
            id: partForm.partFormId,
            isRequired: nextRequired,
        });
        if (!isMounted()) {
            return;
        }
        setTogglingPartFormRequiredMap((prev) => ({...prev, [partForm.partFormId]: false}));
        if (response?.resultFlag) {
            setProduct((prevState) => ({
                ...prevState,
                partForms: (prevState.partForms ?? []).map((e) =>
                    e.partFormId === partForm.partFormId
                        ? {...e, isRequired: response.data?.isRequired ?? nextRequired}
                        : e,
                ),
            }));
        }
    }, [isMounted, setProduct]);

    const toggleBoxes = useMemo(() => [
        {
            title: "Is Serialized",
            description: "This indicates whether the product is serialized or not. Serialized products require unique serial numbers for each unit, allowing for better tracking and management of inventory. Non-serialized products do not require individual serial numbers and are typically managed in bulk.",
            value: product?.isSerializable ?? false,
            onChange: toggleProductSerializability,
            disabled: togglingIsSerialized,
        },
        {
            title: "Public to other partners",
            description: "This indicates whether the product is shared to other partners in the system.",
            value: product?.isPublicForBuyers ?? false,
            onChange: toggleProductShareStatus,
            disabled: togglingIsPublic,
        },
        {
            title: "Visible in the system",
            description: "This indicates whether the product is visible for searching in the entire system, meaning Operator platform and Customer facing website. Note that even if invisible, the product can still be searched and viewed in the \"Pdoducts List\" page",
            value: product?.isVisible ?? false,
            onChange: toggleProductVisibility,
            disabled: togglingIsVisible,
        },
        {
            title: "Visible for Customer website",
            description: "This indicates whether the product is visible for searching in the Customer facing website, for more granular control. Note that the product must be visible in the system in order to be visible for the Customer website, but it can be visible in the system without being visible for the Customer website.",
            value: product?.isVisibleForClient ?? false,
            onChange: toggleIsVisibleForClient,
            disabled: togglingIsVisibleForClient,
        },
        {
            title: "Eligible for Online Ordering",
            description: "This indicates whether the product is eligible for online ordering. Products that are not eligible for online ordering cannot be ordered through the Customer facing website, but can still be ordered through other channels or the Operator platform.",
            value: product?.isEligibleForOnlineOrdering ?? false,
            onChange: toggleIsEligibilityForOnlineOrdering,
            disabled: togglingIsEligibilityForOnlineOrdering,
        }
    ], [product?.isEligibleForOnlineOrdering, product?.isPublicForBuyers, product?.isSerializable, product?.isVisible, product?.isVisibleForClient, toggleIsEligibilityForOnlineOrdering, toggleIsVisibleForClient, toggleProductSerializability, toggleProductShareStatus, toggleProductVisibility, togglingIsEligibilityForOnlineOrdering, togglingIsPublic, togglingIsSerialized, togglingIsVisible, togglingIsVisibleForClient])

    return (
        <>
            <Box sx={{width: '100%', mb: 2}}>
                {
                    loading &&
                    <Grid2 container spacing={3}>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3}}>
                                <Grid2 container spacing={4}>
                                    {
                                        Array(4).fill(null).map((e, index) => (
                                            <Grid2 key={index} xs={12} md={6} xl={4}>
                                                <Skeleton sx={{transform: 'none'}} height={56}/>
                                            </Grid2>
                                        ))
                                    }
                                </Grid2>
                            </Card>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3}}>
                                <Grid2 container spacing={4}>
                                    {
                                        Array(6).fill(null).map((e, index) => (
                                            <Grid2 key={index} xs={12} md={6} xl={4}>
                                                <Skeleton sx={{transform: 'none'}} height={90}/>
                                            </Grid2>
                                        ))
                                    }
                                </Grid2>
                            </Card>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3}}>
                                <Skeleton sx={{transform: 'none'}} height={450}/>
                            </Card>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3, height: '100%'}}>
                                <Grid2 container spacing={3}>
                                    <Grid2 xs={12} md={6} sx={{display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'space-between'}}>
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'flex-start'}}>
                                            {
                                                Array(12).fill(null).map((_, e) => (
                                                    <Skeleton key={e} sx={{transform: 'none'}} width={105} height={38}/>
                                                ))
                                            }
                                        </Box>
                                        <Box>
                                            <Skeleton sx={{transform: 'none'}} width={'100%'} height={56}/>
                                        </Box>
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'flex-start'}}>
                                            {
                                                Array(12).fill(null).map((_, e) => (
                                                    <Skeleton key={e} sx={{transform: 'none'}} width={105} height={38}/>
                                                ))
                                            }
                                        </Box>
                                    </Grid2>
                                </Grid2>
                            </Card>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3, height: '100%'}}>
                                <Grid2 container spacing={3}>
                                    <Grid2 xs={12} md={6} sx={{display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'space-between'}}>
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'flex-start'}}>
                                            {
                                                Array(12).fill(null).map((_, e) => (
                                                    <Skeleton key={e} sx={{transform: 'none'}} width={105} height={38}/>
                                                ))
                                            }
                                        </Box>
                                        <Box>
                                            <Skeleton sx={{transform: 'none'}} width={'100%'} height={56}/>
                                        </Box>
                                    </Grid2>
                                </Grid2>
                            </Card>
                        </Grid2>
                    </Grid2>
                }
                {
                    !loading &&
                    <Grid2 container spacing={3}>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3}}>
                                <Grid2 container spacing={4}>
                                    {
                                        toggleBoxes.map((e) => (
                                            <Grid2 key={e.title} xs={12} lg={6} paddingInline={3}>
                                                <FormControlLabel
                                                    label={
                                                        <Fragment>
                                                            {e.title}
                                                            <Tooltip title={e.description}>
                                                                <InfoOutlined sx={{marginInlineStart: 1}} color={'primary'}/>
                                                            </Tooltip>
                                                        </Fragment>
                                                    }
                                                    slotProps={{typography: {variant: 'body1', color: 'text.primary', fontWeight: 600}}}
                                                    sx={{margin: 0, width: '100%', justifyContent: 'space-between', alignItems: 'center'}}
                                                    labelPlacement={'start'}
                                                    control={
                                                        <Switch
                                                            disabled={e.disabled}
                                                            disableRipple
                                                            checked={e.value}
                                                            onChange={(val) => e.onChange(val)}
                                                        />
                                                    }
                                                />
                                            </Grid2>
                                        ))
                                    }
                                </Grid2>
                            </Card>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3}}>
                                <Grid2 container spacing={2}>
                                    <Grid2 xs={12} md={4} xl={4}>
                                        <ConfigurableInfoBlock
                                            className={'disabled'}
                                            title={'Part Number'}
                                            showHistoryIcon={false}
                                            onHistoryIconClicked={() => false}
                                            inputProps={{
                                                placeholder: 'e.g. ABCD1234',
                                                value: product?.partNo ?? '',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={8} xl={8}>
                                        <ConfigurableInfoBlock
                                            title={'Title'}
                                            showHistoryIcon={false}
                                            onHistoryIconClicked={() => false}
                                            onValueChanged={updatePartTitle}
                                            inputProps={{
                                                placeholder: 'e.g. ABCD1234',
                                                value: product?.title ?? '',
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={4} xl={4}>
                                        <ConfigurableInfoBlock
                                            title={'UPC Number'}
                                            showHistoryIcon={false}
                                            onHistoryIconClicked={() => false}
                                            className={'disabled'}
                                            onValueChanged={updatePartUPC}
                                            inputProps={{
                                                placeholder: 'e.g. 54639667',
                                                value: product?.upc ?? '',
                                                disabled: true,
                                            }}
                                            showCustomIcons
                                            customIcons={[
                                                <IconButton
                                                    key={'edit-upc'}
                                                    component={Link}
                                                    to={routeFunctions.products.single(productId, ProductInformationTabs.codes)}
                                                    color={'primary'}
                                                >
                                                    <OpenInNew className='icon normal'/>
                                                </IconButton>
                                            ]}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={4} xl={4}>
                                        <ConfigurableInfoBlock
                                            title={'Current Cost'}
                                            showHistoryIcon={false}
                                            showChartIcon={false}
                                            className={'disabled'}
                                            onHistoryIconClicked={() => false}
                                            onChartIconClicked={() => setOpenCostChart(true)}
                                            onValueChanged={updatePartCost}
                                            inputProps={{
                                                placeholder: 'e.g. 123.123',
                                                type: 'number',
                                                value: product?.cost ?? '',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12} md={4} xl={4}>
                                        <ConfigurableInfoBlock
                                            title={'Average Cost'}
                                            className={'disabled'}
                                            showHistoryIcon={false}
                                            showChartIcon={false}
                                            onHistoryIconClicked={() => false}
                                            onChartIconClicked={() => setOpenCostChart(true)}
                                            onValueChanged={() => void 0}
                                            inputProps={{
                                                placeholder: 'e.g. 123.123',
                                                type: 'number',
                                                value: product?.avgCost ?? '',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <ConfigurableInfoBlock
                                            title={'Category'}
                                            showHistoryIcon={false}
                                            feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                                            inputType={ConfigurableInfoBlockInputTypes.category}
                                            onHistoryIconClicked={() => false}
                                            onValueChanged={updatePartCategory}
                                            inputProps={{
                                                placeholder: 'e.g. ABCD1234',
                                                value: product?.category?.id,
                                                industryId: product?.industryId,
                                                fallbackCategory: product?.category,
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <ConfigurableInfoBlock
                                            title={t('deliveryMode')}
                                            showHistoryIcon={false}
                                            feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                                            inputType={ConfigurableInfoBlockInputTypes.select}
                                            onHistoryIconClicked={() => false}
                                            onValueChanged={updatePartDeliveryMode}
                                            inputProps={{
                                                value: product?.deliveryMode ?? '',
                                                data: deliveryModeOptions,
                                                SelectProps: {
                                                    renderValue: (selectedValue) => (
                                                        <p>{getDeliveryModeLabel(selectedValue) ?? ''}</p>
                                                    ),
                                                },
                                                disabled: loading || product?.deliveryMode === undefined || product?.deliveryMode === null,
                                            }}
                                        />
                                    </Grid2>
                                </Grid2>
                            </Card>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Card variant={'outlined'} sx={{padding: 3}}>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 4,}}>
                                    <ConfigurableInfoBlock
                                        title={'Short Description'}
                                        showHistoryIcon={false}
                                        feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                                        inputType={ConfigurableInfoBlockInputTypes.input}
                                        onHistoryIconClicked={() => false}
                                        onValueChanged={updateShortDescription}
                                        inputProps={{
                                            placeholder: 'short description...',
                                            value: product?.shortDescription ?? '',
                                        }}
                                    />
                                    <ConfigurableInfoBlock
                                        title={'Description'}
                                        showHistoryIcon={false}
                                        feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                                        inputType={ConfigurableInfoBlockInputTypes.ckEditor}
                                        onHistoryIconClicked={() => false}
                                        onValueChanged={updatePartDescription}
                                        inputProps={{
                                            placeholder: 'description...',
                                            value: product?.description ?? '',
                                        }}
                                    />
                                </Box>
                            </Card>
                        </Grid2>
                        {
                            operatorAccessLevels.packages.lookup.manageTags &&
                            <Grid2 xs={12}>
                                <Card variant={'outlined'} sx={{padding: 3, height: '100%'}}>
                                    <Grid2 container spacing={3}>
                                        <Grid2 xs={12} md={6}>
                                            <ConfigurableInfoBlock
                                                title={'Tags'}
                                                className={'h-100'}
                                                showHistoryIcon={false}
                                                inputType={ConfigurableInfoBlockInputTypes.tagsAutoComplete}
                                                feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                                                onHistoryIconClicked={() => false}
                                                onValueChanged={(value) => addPartTag(value)}
                                                inputProps={{
                                                    fullWidth: true,
                                                    label: 'New tag',
                                                    removeOnCommit: true,
                                                    clearOnBlur: true,
                                                    selectedTags: product?.tags ?? [],
                                                    industryId: product?.industryId,
                                                }}
                                            >
                                                <div className={'flex-fill mb-3'}>
                                                    <div className={'d-flex flex-wrap chips-container'}>
                                                        {product?.tags?.map(tag => (
                                                            <Chips
                                                                key={tag.id}
                                                                className={'mr-2 mt-2'}
                                                                contentType={ChipsContentType.element}
                                                                type={ChipsTypes.remove}
                                                                iconPosition={ChipsIconPosition.start}
                                                                disabled={loading}
                                                                onIconClick={() => removePartTag(tag)}
                                                                buttonProps={{size: 'small',}}
                                                                content={<TagChipsContent tag={tag} useRepeatCount useIcon industryId={product?.industryId}/>}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </ConfigurableInfoBlock>
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            {
                                                suggestedTags?.length > 0 &&
                                                <ConfigurableInfoBlock
                                                    title={'Tag Suggestions'}
                                                    hideInput
                                                >
                                                    <div className={'flex-fill'}>
                                                        <div className={'d-flex flex-wrap chips-container'}>
                                                            {suggestedTags?.map(tag => (
                                                                <Chips
                                                                    key={tag.id}
                                                                    className={'mr-2 mt-2'}
                                                                    contentType={ChipsContentType.element}
                                                                    type={ChipsTypes.add}
                                                                    iconPosition={ChipsIconPosition.start}
                                                                    disabled={loading}
                                                                    onIconClick={() => addPartTag(tag)}
                                                                    buttonProps={{size: 'small'}}
                                                                    content={<TagChipsContent tag={tag} useRepeatCount useIcon industryId={product?.industryId}/>}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </ConfigurableInfoBlock>
                                            }
                                        </Grid2>
                                    </Grid2>
                                </Card>
                            </Grid2>
                        }
                        {
                            operatorAccessLevels.packages.lookup.manageProductForms &&
                            <Grid2 xs={12}>
                                <Card variant={'outlined'} sx={{padding: 3, height: '100%'}}>
                                    <Grid2 container spacing={3}>
                                        <Grid2 xs={12} md={6}>
                                            <ConfigurableInfoBlock
                                                title={'Associated Forms'}
                                                className={'h-100'}
                                                showHistoryIcon={false}
                                                inputType={ConfigurableInfoBlockInputTypes.formsAutoComplete}
                                                feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withTitle}
                                                onHistoryIconClicked={() => false}
                                                onValueChanged={(value) => addPartForm(value)}
                                                inputProps={{
                                                    fullWidth: true,
                                                    label: 'New Association',
                                                    removeOnCommit: true,
                                                    clearOnBlur: true,
                                                    api: formsApi,
                                                    filterOptions: (options) => options.filter((o) => partForms.findIndex((f) => f.form?.id === o.id) === -1,),
                                                }}
                                            >
                                                <div className={'flex-fill mb-3'}>
                                                    <div className={'d-flex flex-wrap chips-container'}>
                                                        {partForms.map((partForm) => (
                                                            <Chips
                                                                key={partForm.partFormId}
                                                                className={'mr-2 mt-2'}
                                                                contentType={ChipsContentType.element}
                                                                type={ChipsTypes.remove}
                                                                iconPosition={ChipsIconPosition.start}
                                                                disabled={loading}
                                                                onIconClick={() => removePartForm(partForm)}
                                                                buttonProps={{size: 'small'}}
                                                                content={
                                                                    <PartFormChipsContent
                                                                        partForm={partForm}
                                                                        disabled={loading}
                                                                        togglingRequired={togglingPartFormRequiredMap[partForm.partFormId]}
                                                                        onToggleRequired={togglePartFormRequired}
                                                                    />
                                                                }
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </ConfigurableInfoBlock>
                                        </Grid2>
                                    </Grid2>
                                </Card>
                            </Grid2>
                        }
                        {
                            operatorAccessLevels.packages.sales.viewPriceMatrices &&
                            <Grid2 xs={12}>
                                <Card variant={'outlined'} sx={{padding: 3}}>
                                    <ProductApplicablePriceMatrices
                                        partId={productId}
                                        categoryId={product?.category?.id}
                                        industryId={product?.industryId}
                                    />
                                </Card>
                            </Grid2>
                        }
                    </Grid2>
                }
            </Box>
            <ProductEntryHistoryDialog
                title={"Cost History"}
                open={openCostChart}
                close={() => setOpenCostChart(false)}
                getHistory={getPartCostHistory}
                getHistoryForExport={getPartCostHistoryForExport}
                hideExport={!operatorAccessLevels.packages.lookup.exportProductCostHistory}
            />
        </>
    )
}

export default ProductInformationBasicInformationView;
